import { toaster } from '@/components/ui/toaster';
import { invalidateBrandSpaceProgramOptions } from '@/hooks/queries/use-brand-space-program-options-query';
import api from '@/libs/axiosInstance';
import type { ApiResponse } from '@fluenceur/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';

const deleteProgram = async (programId: string): Promise<void> => {
  const response = await api.delete<ApiResponse>(`/dashboard/programs/${programId}`);

  if (response.data?.success !== true) {
    throw new Error(response.data?.message || 'Failed to delete program');
  }
};

export function useDeleteProgramAction() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: deleteProgram,
    onSuccess: (_, programId) => {
      // Invalidate and refetch program-related queries
      queryClient.invalidateQueries({ queryKey: ['program', programId] });
      queryClient.invalidateQueries({ queryKey: ['programs'] });
      queryClient.invalidateQueries({ queryKey: ['affiliate-programs'] });
      void invalidateBrandSpaceProgramOptions(queryClient);

      // Remove the deleted program from cache
      queryClient.removeQueries({ queryKey: ['program', programId] });

      toaster.success({
        title: 'Program deleted successfully',
        description: 'The program has been permanently deleted.',
      });
    },
    onError: (error) => {
      toaster.error({
        title: 'Failed to delete program',
        description: error.message,
      });
    },
  });
}
